import * as React from 'react';
import cx from 'classnames';
import { I18n } from 'react-redux-i18n';

import { useController } from './pedigrees-table.controller';
import { PedigreesTableProps } from './pedigrees-table.types';
import { NewTable, NewTableSelection } from '@common/components/new-table';
import { MODULE_NAME } from '@modules/pedigrees/constants/shared';
import { ProfileSelector } from '@common/components/profile-selector';
import { CopyPedigree } from './components/copy-pedigree';
import { MovePedigree } from './components/move-pedigree';
import { CreateShortcut } from './components/create-shortcut';
import { PedigreeRenaming } from './components/pedigree-renaming';
import { GenerateExternalLink } from '@modules/pedigrees/components/pedigrees-table/components/generate-external-link';
import { MessageBox } from '@common/components/MessageBox';
import { ItemSecurityManagement } from '@common/components/item-security-management';

import './pedigrees-table.styles.scss'

export const PedigreesTable: React.FC<PedigreesTableProps> = (props) => {
  const { filters, itemUniqProperty, selectedItems, data, totalItemsCount, loadMetadata, className, loading, selectedFolderId, onSelectedItemsChange, showErrorMessage, getFoldersForPedigreeActions, getPedigreeExternalLink, getFoldersForAddingColumns } = props;

  const {
    changingColumns,
    renamePedigreeProps,
    closeRenamePedigree,
    handleRenamePedigree,
    createShortcutProps,
    closeCreateShortcut,
    handleCreateShortcut,
    movePedigreeProps,
    closeMovePedigree,
    handleMovePedigree,
    handleCopyPedigree,
    copyPedigreeProps,
    closeCopyPedigree,
    handleColumnAdd,
    handleColumnRemove,
    handleGetFields,
    getHeaderElementId,
    getHeaderCellElementId,
    getBodyRowElementId,
    getBodyCellElementId,
    getProfiles,
    handleProfileSelectorSubmit,
    closeProfileSelector,
    profileSelectorOpened,
    generateExternalLinkOpened,
    selectedPedigreeId,
    generateExternalLinkMessage,
    columnActions,
    headerActions,
    tableColumns,
    getData,
    handleColumnsResizing,
    handleColumnsOrderChange,
    getPedigreeSecurity,
    updatePedigreeSecurity,
    closePedigreeSecurity,
    pedigreeSecurityOpened
  } = useController(props);

  return (
    <>
      {renamePedigreeProps && (
        <PedigreeRenaming
          onClose={closeRenamePedigree}
          onSubmit={handleRenamePedigree}
          showErrorMessage={showErrorMessage}
          defaultValue={renamePedigreeProps.pedigreeName}
        />
      )}
      {copyPedigreeProps && (
        <CopyPedigree
          getFolders={getFoldersForPedigreeActions}
          onSubmit={handleCopyPedigree}
          showErrorMessage={showErrorMessage}
          onClose={closeCopyPedigree}
        />
      )}
      {movePedigreeProps && (
        <MovePedigree
          pedigreeName={movePedigreeProps.pedigreeName}
          getFolders={getFoldersForPedigreeActions}
          onSubmit={handleMovePedigree}
          showErrorMessage={showErrorMessage}
          onClose={closeMovePedigree}
        />
      )}
      {createShortcutProps && selectedFolderId && (
        <CreateShortcut
          folderId={selectedFolderId}
          pedigreeName={createShortcutProps.pedigreeName}
          getFolders={getFoldersForPedigreeActions}
          onSubmit={handleCreateShortcut}
          showErrorMessage={showErrorMessage}
          onClose={closeCreateShortcut}
        />
      )}
      {profileSelectorOpened && (
        <ProfileSelector
          onClose={closeProfileSelector}
          onSubmit={handleProfileSelectorSubmit}
          showErrorMessage={showErrorMessage}
          getData={getProfiles}
        />
      )}
      {pedigreeSecurityOpened && (
        <ItemSecurityManagement
          getId={{
            title:'lbl-ped-sec-close',
            cancelBtn: 'btn-ped-sec-cancel',
            submitBtn: 'btn-ped-sec-save',
          }}
          title={I18n.t("Edit Pedigree Security")}
          overriddenSubtitle={I18n.t('Security for this pedigree is overridden at the folder level')}
          getSecurity={getPedigreeSecurity}
          onSubmit={updatePedigreeSecurity}
          onClose={closePedigreeSecurity}
          showErrorMessage={showErrorMessage}
        />
      )}
      <MessageBox
        show={generateExternalLinkOpened}
        {...generateExternalLinkMessage}
        content={
          selectedPedigreeId && (
            <GenerateExternalLink
              showErrorMessage={showErrorMessage}
              getData={getPedigreeExternalLink}
              selectedPedigreeId={selectedPedigreeId}
            />
          )
        }
      />
      <NewTable
        infiniteScroll
        disableAutoInit
        classes={{
          root: cx("PedigreesTable", className),
        }}
        columnsResizing={{
          onChange: handleColumnsResizing,
        }}
        columnsOrder={{
          onChange: handleColumnsOrderChange,
        }}
        getId={{
          header: getHeaderElementId,
          headerCell: getHeaderCellElementId,
          bodyRow: getBodyRowElementId,
          bodyCell: getBodyCellElementId,
        }}
        selection={{
          type: "multiple",
          items: selectedItems,
          onChange: onSelectedItemsChange as NewTableSelection["onChange"],
        }}
        columnsManagement={{
          onAdd: handleColumnAdd,
          onRemove: handleColumnRemove,
          getFolders: getFoldersForAddingColumns,
          getFields: handleGetFields,
        }}
        filters={filters}
        columnActions={columnActions}
        headerActions={headerActions}
        loading={{ list: !loadMetadata || loading, columns: changingColumns }}
        moduleName={`${MODULE_NAME}_pedigrees_table`}
        noDataMessage={I18n.t('There no pedigrees to display')}
        errorMessage={{ list: I18n.t('Failed to load pedigrees') }}
        itemUniqProperty={itemUniqProperty}
        data={data}
        columns={tableColumns}
        totalItemsCount={totalItemsCount}
        loadMetadata={loadMetadata}
        onLoadData={getData}
      />
    </>
  );
};